export const _geocodeDelay = () => new Promise(r => setTimeout(r, 1100));

// ─── What may be stored as a pin ─────────────────────────────────────────
//
// A pin is a fact about the ground: once it is saved, nothing re-derives it.
// So the one geocoder whose answer gets SAVED - geocodeAddress - is strict, and
// returns nothing rather than a guess. Before 2026-09-29 it restricted results
// to the US and took Google's first answer, which put Proxy records on a
// same-named street in whichever state Google preferred. The Python loaders
// hit the same fault through their cache; the rules below mirror theirs
// (Map/load_coordinates_to_proxy.py: FOOTPRINT, in_footprint, same_street).
//
//   1. The state is read off the address and ENFORCED (components=
//      administrative_area) - a hard filter, not a hint.
//   2. The answer must land in a state Proxy has a market in.
//   3. If the address has a house number, the answer must carry that number:
//      a route-only centroid ("Avenue E, Alabama") is not a location.
//   4. The street must agree, allowing for how two systems spell it.
//   5. APPROXIMATE precision is refused.
//
// geocodeForView is the lenient one, for the map's search box and radius
// centre: it moves the view and saves nothing, so a looser answer is fine.

/** Every state Proxy has a market in. Mirror of FOOTPRINT in the Map/ loaders. */
export const FOOTPRINT = ["FL", "TX", "GA", "TN", "NC", "SC"];

const STATE_NAMES = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO",
  connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID",
  illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI", minnesota: "MN",
  mississippi: "MS", missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
  "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC", "south dakota": "SD",
  tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT", virginia: "VA", washington: "WA",
  "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
};
const STATE_CODES = new Set(Object.values(STATE_NAMES));

/** Two-letter state named in an address string, or null. The last one wins. */
export function stateFromAddress(address) {
  const a = String(address || "");
  let found = null;
  for (const m of a.matchAll(/(?:^|[,\s])([A-Z]{2})(?=\s+\d{5}\b|\s*,|\s*$)/g)) {
    if (STATE_CODES.has(m[1])) found = m[1];
  }
  if (found) return found;
  // Spelled out - "Medley, Florida 33166". Must stand as its own part of the
  // address, so "Washington Ave" is not read as the state of Washington.
  let at = -1;
  for (const [name, code] of Object.entries(STATE_NAMES)) {
    const re = new RegExp(`(?:^|[,\\s])${name}(?=\\s+\\d{5}\\b|\\s*,|\\s*$)`, "gi");
    for (const m of a.matchAll(re)) {
      if (m.index > at) { at = m.index; found = code; }
    }
  }
  return found;
}

const DIRS = { north: "n", south: "s", east: "e", west: "w", northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw" };
const TYPES = new Set(["st", "street", "rd", "road", "dr", "drive", "ave", "avenue", "blvd", "boulevard",
  "ct", "court", "ln", "lane", "way", "pkwy", "parkway", "hwy", "highway", "cir", "circle", "pl", "place",
  "ter", "terrace", "trl", "trail", "loop", "pike", "row", "sq", "square", "plz", "plaza"]);

/** Street name reduced to bare letters and digits - ordinals, types, spacing removed. */
function streetKey(s) {
  return String(s || "").toLowerCase().replace(/[^\w\s]/g, "").split(/\s+/).filter(Boolean)
    .map(w => w.replace(/^(\d+)(st|nd|rd|th)$/, "$1"))
    .map(w => DIRS[w] || w)
    .filter(w => !TYPES.has(w))
    .join("");
}

/** House number and the rest of the street, from the first part that opens with a number. */
function splitStreet(address) {
  for (const part of String(address || "").split(",")) {
    const m = part.trim().match(/^(\d+)[\d-]*\s+(.*)$/);
    if (m) return { num: m[1], street: m[2] };
  }
  return { num: null, street: String(address || "").split(",")[0] };
}

function component(result, type) {
  const c = (result.address_components || []).find(x => (x.types || []).includes(type));
  return c ? c.short_name : null;
}

/**
 * The geocoder for anything that will be SAVED. Returns {lat, lng} only when
 * every rule above holds; otherwise null.
 */
export async function geocodeAddress(address) {
  if (!address || !address.trim()) return null;
  const want = stateFromAddress(address);
  const { num, street } = splitStreet(address);
  const googleKey = process.env.REACT_APP_GOOGLE_GEOCODE_KEY;

  if (googleKey) {
    try {
      const comps = "country:US" + (want ? `|administrative_area:${want}` : "");
      const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}`
        + `&components=${encodeURIComponent(comps)}&key=${googleKey}`);
      if (resp.ok) {
        const data = await resp.json();
        const r = data.status === "OK" && data.results[0];
        if (r) {
          const st = component(r, "administrative_area_level_1");
          const gotNum = component(r, "street_number");
          const route = component(r, "route");
          const precise = r.geometry?.location_type && r.geometry.location_type !== "APPROXIMATE";
          const stateOk = FOOTPRINT.includes(st) && (!want || st === want);
          const numOk = !num || (gotNum && gotNum.split("-")[0] === num);
          const a = streetKey(street), b = streetKey(route);
          const streetOk = !num || (a && b && (a.includes(b) || b.includes(a)));
          if (precise && stateOk && numOk && streetOk) {
            return { lat: r.geometry.location.lat, lng: r.geometry.location.lng };
          }
        }
      }
    } catch (e) {}
  }

  // Census Bureau fallback - its matches are street-address matches, so the
  // number is inherent; only the state needs checking.
  const parts = address.split(",").map(s => s.trim());
  const params = new URLSearchParams({
    street: parts[0] || address, city: parts[1] || "", state: want || (parts[2] || "").replace(/\s+\d{5}.*/, "").trim(),
    benchmark: "2020", format: "json",
  });
  try {
    const resp = await fetch("https://geocoding.geo.census.gov/geocoder/locations/address?" + params);
    if (resp.ok) {
      const data = await resp.json();
      const m = (data?.result?.addressMatches || [])[0];
      const st = m?.addressComponents?.state;
      if (m && FOOTPRINT.includes(st) && (!want || st === want)) {
        return { lat: parseFloat(m.coordinates.y), lng: parseFloat(m.coordinates.x) };
      }
    }
  } catch (e) {}
  return null;
}

/**
 * Lenient geocoder for moving the MAP VIEW only - the search box and the
 * radius centre. Saves nothing, so it may take Google's first answer.
 */
export async function geocodeForView(address) {
  if (!address || !address.trim()) return null;
  const googleKey = process.env.REACT_APP_GOOGLE_GEOCODE_KEY;
  if (!googleKey) return geocodeAddress(address);
  try {
    const resp = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&components=country:US&key=${googleKey}`);
    if (resp.ok) {
      const data = await resp.json();
      if (data.status === "OK" && data.results.length > 0) {
        const loc = data.results[0].geometry.location;
        return { lat: loc.lat, lng: loc.lng };
      }
    }
  } catch (e) {}
  return null;
}

export async function geocodeWithNominatim(address) {
  if (!address || !address.trim()) return null;
  try {
    const resp = await fetch(
      "https://nominatim.openstreetmap.org/search?q=" + encodeURIComponent(address) + "&format=json&limit=1&countrycodes=us",
      { headers: { "User-Agent": "ProximityParks-Proxy/1.0" } }
    );
    if (resp.ok) {
      const data = await resp.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    }
  } catch (e) { console.warn("Nominatim full address failed:", e); }
  // Fallback: city, state, zip only
  const parts = address.split(",").map(s => s.trim());
  if (parts.length >= 2) {
    const fallback = parts.slice(1).join(", ");
    try {
      await _geocodeDelay();
      const resp = await fetch(
        "https://nominatim.openstreetmap.org/search?q=" + encodeURIComponent(fallback) + "&format=json&limit=1&countrycodes=us",
        { headers: { "User-Agent": "ProximityParks-Proxy/1.0" } }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data && data.length > 0) {
          return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        }
      }
    } catch (e) { console.warn("Nominatim fallback failed:", e); }
  }
  return null;
}
